/**
 * useCanvasBreakpoint (spec section 7.2 consumption).
 *
 * Reports the current responsive breakpoint (desktop / tablet / phone) for the
 * live viewport, updating on window resize. It is the runtime input to the
 * deterministic responsive mapping in responsiveLayout.ts: the render path reads
 * this hook, then calls effectiveColumns()/mapScope() to place elements for the
 * current breakpoint.
 *
 * Server / no-window default is desktop, so server-rendered and first-paint
 * output matches the authored (desktop) layout and there is no hydration jump on
 * a desktop client. When `enabled` is false the hook is inert (always desktop),
 * which lets a caller keep the hook call unconditional -- as the Rules of Hooks
 * require -- while turning responsiveness on or off with a prop.
 */

import { useSyncExternalStore } from "react";
import {
  DESKTOP_MIN_WIDTH,
  resolveBreakpointKind,
  type BreakpointKind,
} from "./breakpoints";

/** Current viewport width, or the desktop floor when there is no window (SSR). */
function readViewportWidth(): number {
  return typeof window !== "undefined" ? window.innerWidth : DESKTOP_MIN_WIDTH;
}

/** Subscribe to viewport width changes. Stable identity for useSyncExternalStore. */
function subscribeToResize(onChange: () => void): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }
  window.addEventListener("resize", onChange);
  return () => window.removeEventListener("resize", onChange);
}

/**
 * Returns the current responsive breakpoint kind for the live viewport.
 *
 * @param enabled When false, always returns "desktop" (identity layout) and does
 *   not react to resize. Defaults to true.
 */
export function useCanvasBreakpoint(enabled = true): BreakpointKind {
  const width = useSyncExternalStore(
    subscribeToResize,
    () => (enabled ? readViewportWidth() : DESKTOP_MIN_WIDTH),
    () => DESKTOP_MIN_WIDTH,
  );
  return resolveBreakpointKind(width);
}
